import { useCallback, useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import "../styles/Gallery.css";

// Fotos de trabajos reales
const galleryImages = [
  {
    id: 1,
    src: "images/HouseCleaning/house1.jpg",
    alt: "Beautiful house, clean and ready to move in",
    category: "house"
  },
  {
    id: 2,
    src: "images/HouseCleaning/house2.jpg",
    alt: "Complete service for a clean and comfortable office",
    category: "office"
  },
  {
    id: 3,
    src: "images/HouseCleaning/house3.jpg",
    alt: "Beautiful house, clean and ready to move in",
    category: "house"
  },
  {
    id: 4,
    src: "images/OfficeImages/office1.jpg",
    alt: "Clean and tidy office cubicle area",
    category: "office"
  },
  {
    id: 5,
    src: "images/OfficeImages/office2.jpg",
    alt: "Clean and tidy sales area",
    category: "office"
  },
  {
    id: 6,
    src: "images/OfficeImages/office3.jpg",
    alt: "Fully renovated manager's office",
    category: "office"
  },
  {
    id: 7,
    src: "images/OfficeImages/office4.jpg",
    alt: "Professional carpet vacuuming for office use",
    category: "office"
  },
  {
    id: 8,
    src: "images/OfficeImages/office5.jpg",
    alt: "Children's area clean and ready for use",
    category: "office"
  },
  {
    id: 9,
    src: "images/WindowCleaning/window2.jpg",
    alt: "Sparkling clean windows for your business",
    category: "window"
  }

];

const filters = [
  { key: 'all', label: 'All' },
  { key: 'house', label: 'House' },
  { key: 'office', label: 'Office' },
  { key: 'window', label: 'Windows' }
];

const countFor = (key) =>
  key === 'all'
    ? galleryImages.length
    : galleryImages.filter((img) => img.category === key).length;

// Componente principal de la galería
export default function GalleryComponent() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  // Filtrar imágenes según la categoría seleccionada
  const filteredImages = activeFilter === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeFilter);

  const currentIndex = selectedImage
    ? filteredImages.findIndex(img => img.id === selectedImage.id)
    : -1;

  // Abrir la imagen en modo lightbox
  const openLightbox = (image) => setSelectedImage(image);

  // Cerrar el lightbox
  const closeLightbox = useCallback(() => setSelectedImage(null), []);

  // Navegar entre imágenes
  const step = useCallback((delta) => {
    setSelectedImage((current) => {
      if (!current) return current;
      const index = filteredImages.findIndex(img => img.id === current.id);
      if (index === -1) return current;
      const next = (index + delta + filteredImages.length) % filteredImages.length;
      return filteredImages[next];
    });
  }, [filteredImages]);

  // Teclado: Escape para cerrar, flechas para navegar
  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedImage, closeLightbox, step]);

  return (
    <section className="sec sec--porcelain" id='ImageGallery'>
      <div className="container">
        {/* Encabezado de la galería */}
        <div className="sec-head sec-head--center">
          <span className="eyebrow">Finished work</span>
          <h2 className="sec-title">Rooms we handed back</h2>
          <p className="sec-lede">
            Homes, offices and storefronts photographed after our crews left.
          </p>
        </div>

        {/* Filtros de categoría */}
        <div className="gfilters">
          {filters.map(({ key, label }) => (
            <button
              type="button"
              key={key}
              className={`gfilter${activeFilter === key ? ' is-active' : ''}`}
              onClick={() => setActiveFilter(key)}
              aria-pressed={activeFilter === key}
            >
              {label}
              <span className="gfilter__count">{countFor(key)}</span>
            </button>
          ))}
        </div>

        {/* Cuadrícula de imágenes */}
        <div className="ggrid">
          {filteredImages.map((image) => (
            <button
              type="button"
              key={image.id}
              className="gtile gleam"
              onClick={() => openLightbox(image)}
              aria-label={`Open image: ${image.alt}`}
            >
              <img src={image.src} alt={image.alt} loading="lazy" />
              <span className="gtile__veil">{image.alt}</span>
              <span className="gtile__zoom" aria-hidden="true">
                <Maximize2 size={16} />
              </span>
            </button>
          ))}
        </div>

        {filteredImages.length === 0 && (
          <p className="gempty">No photos in this category yet.</p>
        )}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="glightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLightbox();
          }}
        >
          <button
            type="button"
            className="glightbox__btn glightbox__btn--close"
            onClick={closeLightbox}
            aria-label="Close viewer"
          >
            <X size={20} />
          </button>

          {filteredImages.length > 1 && (
            <button
              type="button"
              className="glightbox__btn glightbox__btn--prev"
              onClick={() => step(-1)}
              aria-label="Previous image"
            >
              <ChevronLeft size={22} />
            </button>
          )}

          <figure className="glightbox__figure">
            <img
              key={selectedImage.id}
              src={selectedImage.src}
              alt={selectedImage.alt}
            />
            <figcaption className="glightbox__caption">
              {selectedImage.alt}
              <span className="glightbox__counter">
                {currentIndex + 1} / {filteredImages.length}
              </span>
            </figcaption>
          </figure>

          {filteredImages.length > 1 && (
            <button
              type="button"
              className="glightbox__btn glightbox__btn--next"
              onClick={() => step(1)}
              aria-label="Next image"
            >
              <ChevronRight size={22} />
            </button>
          )}
        </div>
      )}
    </section>
  );
}
